/**
 * Adresele recente ale acestui browser — „contul" de adrese al unei platforme
 * fara conturi.
 *
 * Wolt si Glovo tin adresele pe cont; aici clientul nu are cont (checkout ca
 * oaspete, ADR-015), deci singurul loc in care o adresa poate fi retinuta e
 * dispozitivul. Se retin cel mult cinci, cea mai recenta prima, si se ofera in
 * fereastra de adresa ca alegeri dintr-o apasare: a doua comanda nu mai cere
 * strada, numarul, blocul si interfonul inca o data.
 *
 * Modul e PUR (fara `window`): cine il foloseste ii da textul din stocare si
 * primeste inapoi lista de scris. Asa se poate testa dedublarea si plafonul.
 */

import type { DropoffAddressDto } from "@deliveryapp/shared";

export const RECENT_ADDRESSES_MAX = 5;
const STORAGE_PREFIX = "deliveryapp.addresses.v1";

export function recentAddressesStorageKey(marketKey: string): string {
  return `${STORAGE_PREFIX}.${marketKey}`;
}

/**
 * Identitatea unei adrese pentru dedublare: punctul (rotunjit la ~10 m) plus
 * apartamentul. Aceeasi cladire cu alt apartament e ALTA adresa — clientul care
 * comanda si la el, si la parintii de la etajul 3, le vrea pe amandoua.
 */
export function addressIdentity(address: DropoffAddressDto): string {
  const lat = Math.round(address.lat * 10_000) / 10_000;
  const lng = Math.round(address.lng * 10_000) / 10_000;
  return `${lat}:${lng}:${(address.apartment ?? "").trim().toLowerCase()}`;
}

function isAddress(value: unknown): value is DropoffAddressDto {
  if (typeof value !== "object" || value === null) return false;
  const address = value as Record<string, unknown>;
  return (
    typeof address.lat === "number" &&
    Number.isFinite(address.lat) &&
    typeof address.lng === "number" &&
    Number.isFinite(address.lng) &&
    typeof address.street === "string" &&
    address.street.length > 0 &&
    typeof address.city === "string"
  );
}

export function parseRecentAddresses(raw: string | null): DropoffAddressDto[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isAddress).slice(0, RECENT_ADDRESSES_MAX);
  } catch {
    return [];
  }
}

/** Lista noua, cu adresa data in fata si dublurile ei scoase. */
export function rememberAddress(list: readonly DropoffAddressDto[], address: DropoffAddressDto): DropoffAddressDto[] {
  const identity = addressIdentity(address);
  return [address, ...list.filter((item) => addressIdentity(item) !== identity)].slice(0, RECENT_ADDRESSES_MAX);
}

export function forgetAddress(list: readonly DropoffAddressDto[], address: DropoffAddressDto): DropoffAddressDto[] {
  const identity = addressIdentity(address);
  return list.filter((item) => addressIdentity(item) !== identity);
}

export function readRecentAddresses(marketKey: string): DropoffAddressDto[] {
  try {
    return parseRecentAddresses(window.localStorage.getItem(recentAddressesStorageKey(marketKey)));
  } catch {
    return [];
  }
}

export function writeRecentAddresses(marketKey: string, list: readonly DropoffAddressDto[]): void {
  try {
    window.localStorage.setItem(recentAddressesStorageKey(marketKey), JSON.stringify(list.slice(0, RECENT_ADDRESSES_MAX)));
  } catch {
    // Stocare indisponibila (mod privat, plina): adresa ramane in sesiune, doar
    // nu se mai ofera data viitoare. Nu e o eroare de aratat clientului.
  }
}
